import type { InjectionKey, Plugin, UnwrapNestedRefs } from "vue";
import type { DeepMaybeRef } from "@vueuse/core";

import type {
  CalculationWorkerRequest,
  CalculationWorkerResponse,
  QueryCalculationPredictionKey
} from "@/libs/calculations/calculationWorker";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";

import { reactive } from "vue";
import { v4 } from "uuid";

import CalculationWorker from "@/libs/calculations/calculationWorker?worker";
import { removeArrayElement } from "@/utils/utils";

type QueryCalculationFn = (
  scenario: DeepMaybeRef<DestructuredScenario>,
  prediction?: QueryCalculationPredictionKey,
  neededCalcs?: { [key: string]: string }
) => Promise<{
  neededCalcs: Record<string, number | null>;
  prediction: number;
}>;

export const calculationWorkerKey: InjectionKey<{
  queryCalculation: QueryCalculationFn;
  cancelCalculations: () => void;
}> = Symbol("calculationWorker");

type CalculationWorkerPluginOptions = {
  minWorkers?: number;
  maxWorkers?: number;
};

// const log = useLogger("Calculation Worker Plugin");

const calculationWorkerPlugin: Plugin = {
  install(app, options?: CalculationWorkerPluginOptions) {
    const MIN_WORKERS = options?.minWorkers ?? 1;
    const MAX_WORKERS =
      options?.maxWorkers ?? navigator.hardwareConcurrency - 1;
    const allWorkers: Worker[] = [];
    const availableWorkers: Worker[] = [];
    const messageQueue: CalculationWorkerRequest[] = [];
    const resolvers: Record<string, (value: any) => void> = {};

    const queryCalculation: QueryCalculationFn = (
      scenario,
      prediction?: QueryCalculationPredictionKey,
      neededCalcs = {}
    ) => {
      const id = v4();

      // Use reactive() to unwrap refs because web workers don't like Vue reactivity
      const scenarioRaw = reactive(scenario);

      return new Promise(resolve => {
        const request: CalculationWorkerRequest = {
          id,
          prediction,
          neededCalcs,
          // Although web workers default to structuredClone(), Proxy type objects
          // are not supported with that algorithm (and likely never will be)
          // https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm
          // Instead, just use a good ol' JSON.parse(JSON.stringify(obj)) deep clone.
          scenario: JSON.parse(JSON.stringify(scenarioRaw))
        };

        enqueueMessage(request, resolve);
      });
    };

    function cancelCalculations() {
      messageQueue.forEach(q => {
        const resolve = resolvers[q.id];
        if (resolve) {
          resolve(null);
          delete resolvers[q.id];
        }
      });

      messageQueue.length = 0;
    }

    function enqueueMessage(
      query: CalculationWorkerRequest,
      resolve: (value: any) => void
    ) {
      // Store resolve() so that our event listener can
      // resolve the promise when worker responds
      resolvers[query.id] = resolve;

      messageQueue.push(query);
      processNextQuery();
    }

    function processNextQuery() {
      adjustWorkerPool();

      if (availableWorkers.length > 0 && messageQueue.length > 0) {
        const worker = availableWorkers.pop() as Worker;
        const msg = messageQueue.shift();

        worker.postMessage(msg);
      }
    }

    function addWorker() {
      if (allWorkers.length < MAX_WORKERS) {
        const worker = new CalculationWorker();

        worker.addEventListener(
          "message",
          (event: MessageEvent<CalculationWorkerResponse>) => {
            const resolve = resolvers[event.data.id];

            if (resolve) {
              resolve(event.data.value);
              delete resolvers[event.data.id];
            }

            availableWorkers.push(worker);
            processNextQuery();
          }
        );

        allWorkers.push(worker);
        availableWorkers.push(worker);
      }
    }

    function removeWorker() {
      while (
        allWorkers.length > messageQueue.length &&
        availableWorkers.length > 0
      ) {
        const worker = availableWorkers.pop();
        if (worker) {
          worker.terminate();
          removeArrayElement(allWorkers, worker);
        }
      }
    }

    function adjustWorkerPool() {
      if (messageQueue.length > availableWorkers.length) {
        addWorker();
      } else if (messageQueue.length < availableWorkers.length) {
        removeWorker();
      }
    }

    // This function causes a browser issue and is removed as part of issue 2188 - this link can be accessed through the browser as well
    // https://chromestatus.com/feature/5579556305502208
    // https://github.com/fergald/docs/blob/master/explainers/permissions-policy-deprecate-unload.md
    // window.onunload = () => {
    //   for (const worker of allWorkers) {
    //     worker.terminate();
    //   }
    // };
    // further reading will lead you here
    // https://developer.chrome.com/docs/web-platform/page-lifecycle-api

    // The following replaces the unload event above. Most of the code is currently commented out
    // but is a copy of that found at the above link. We were unable to fully test the capture of
    // the freeze event as spyglass fully reloads when the tab is refocused after discarding,
    // removing logs left from the frozen event.

    // const getState = () => {
    //   if (document.visibilityState === "hidden") {
    //     return "hidden";
    //   }
    //   if (document.hasFocus()) {
    //     return "active";
    //   }
    //   return "passive";
    // };

    // https://developer.chrome.com/docs/web-platform/page-lifecycle-api#how_to_observe_state_changes
    // Stores the initial state using the `getState()` function (defined above).
    // let state = getState();

    // Accepts a next state and, if there's been a state change, logs the
    // change to the console. It also updates the `state` value defined above.
    // const logStateChange = (nextState: string) => {
    //   const prevState = state;
    //   if (nextState !== prevState) {
    //     console.log(`State change: ${prevState} >>> ${nextState}`);
    //     state = nextState;
    //   }
    // };

    // Options used for all event listeners.
    const opts = { capture: true };

    // These lifecycle events can all use the same listener to observe state
    // changes (they call the `getState()` function to determine the next state).
    // ["pageshow", "focus", "blur", "visibilitychange", "resume"].forEach(
    //   type => {
    //     window.addEventListener(type, () => logStateChange(getState()));
    //   },
    //   opts
    // );

    // The next two listeners, on the other hand, can determine the next
    // state from the event itself.
    window.addEventListener(
      "freeze",
      () => {
        // In the freeze event, the next state is always frozen.
        // logStateChange("frozen");
        for (const worker of allWorkers) {
          worker.terminate();
        }
      },
      opts
    );

    window.addEventListener(
      "pagehide",
      () => {
        // event => {
        // If the event's persisted property is `true` the page is about
        // to enter the back/forward cache, which is also in the frozen state.
        // If the event's persisted property is not `true` the page is
        // about to be unloaded.
        // logStateChange(event.persisted ? "frozen" : "terminated");
        for (const worker of allWorkers) {
          worker.terminate();
        }
      },
      opts
    );

    app.provide(calculationWorkerKey, {
      queryCalculation,
      cancelCalculations
    });
  }
};

export default calculationWorkerPlugin;
